import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
    Box,
    Button,
    Collapse,
    Stack,
    TextField,
    Typography,
    useMediaQuery,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { alpha } from '@mui/material/styles';
import { parseApiDate , toDatetimeLocalString } from 'src/utils/taskDateUtils';
import Iconify from '../Iconify';
import AssignedToLead from '../AssignedToLead';
import { dispatch, useSelector } from '../../redux/store';
import { createTask, pauseLeadCascade, completeTasksInParallel } from '../../redux/slices/taskSlice';
import { getIconAnimationClass, getIconAnimSx } from '../../sections/@dashboard/leadDetail/AttendedGuidedChecklist/v2/UpNextCardV2';
import { updateError } from '../../redux/slices/user';
import { hasFeatureAccess } from '../../utils/featurePermission';
import { TASK_PRIORITY, TASK_CATEGORY, TASK_METHOD } from '../../constants/TaskConstants';
import { ACTION_META } from '../../sections/@dashboard/leadDetail/AttendedGuidedChecklist/actionMeta';
import capitalizeFirstLetter from '../../utils/capitalizedFunc';

// ─── Date chip helpers ────────────────────────────────────────────────────────
// Mirrors the Today/Tomorrow/Next-week pattern in CreateTaskDialog.jsx.

const getToday = () => {
    const d = new Date();
    d.setHours(d.getHours() + 1, 0, 0, 0);
    return toDatetimeLocalString(d);
};

const getTomorrow = () => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    d.setHours(9, 0, 0, 0);
    return toDatetimeLocalString(d);
};

const getNextWeek = () => {
    const d = new Date();
    const daysUntilMon = ((8 - d.getDay()) % 7) || 7;
    d.setDate(d.getDate() + daysUntilMon);
    d.setHours(9, 0, 0, 0);
    return toDatetimeLocalString(d);
};

const DATE_CHIPS = [
    { label: 'Today', getValue: getToday },
    { label: 'Tomorrow', getValue: getTomorrow },
    { label: 'Next week', getValue: getNextWeek },
    { label: 'Custom', getValue: null },
];

// ─── V2 action type chips ─────────────────────────────────────────────────────
// Three curated action types shown in the "How" section.  The backend Task
// model has no action_type / item_type field — those belong to ChecklistItems.
// We therefore map each V2 action type to the closest TaskMethod value so the
// created task still carries a meaningful `method` field.
//
// ACTION_TYPE_OPTIONS drives the chip UI; METHOD_FOR_ACTION drives the payload.
const ACTION_TYPE_OPTIONS = [
    { actionType: 'LOG_PHONE_CALL', label: 'Call',      icon: ACTION_META.LOG_PHONE_CALL.icon,  color: ACTION_META.LOG_PHONE_CALL.color  },
    { actionType: 'WHATSAPP_SENT',  label: 'WhatsApp',  icon: ACTION_META.WHATSAPP_SENT.icon,   color: ACTION_META.WHATSAPP_SENT.color   },
    { actionType: 'EMAIL_SENT',     label: 'Email',     icon: ACTION_META.EMAIL_SENT.icon,      color: ACTION_META.EMAIL_SENT.color      },
];

const METHOD_FOR_ACTION = {
    LOG_PHONE_CALL: TASK_METHOD.CALL,
    WHATSAPP_SENT:  TASK_METHOD.WHATSAPP,
    EMAIL_SENT:     TASK_METHOD.EMAIL,
};

// ─── Shared chip style factory ────────────────────────────────────────────────
// `activeColor` defaults to the CRM green. The "How" action chips pass their
// per-action ACTION_META colour so they match the checklist's colour language;
// every other chip group (When / Category / Priority) keeps the default green.
const chipSx = (active, activeColor = '#00AB55') => ({
    px: 1.5,
    py: 0.5,
    borderRadius: '6px',
    border: '1px solid',
    borderColor: active ? activeColor : 'rgba(0,0,0,0.12)',
    bgcolor: active ? alpha(activeColor, 0.08) : '#ffffff',
    color: active ? activeColor : '#6e6e73',
    fontSize: '13px',
    fontWeight: active ? 600 : 400,
    cursor: 'pointer',
    userSelect: 'none',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 0.5,
    transition: 'all 150ms ease',
    '&:hover': {
        borderColor: activeColor,
        color: activeColor,
        bgcolor: alpha(activeColor, 0.06),
    },
});

// Compact uppercase section label — smaller + tighter than the old 11px/mb:1
// version so the form reads denser and matches the UP NEXT card's micro-labels.
const LABEL_SX = {
    fontSize: '10px',
    fontWeight: 600,
    color: '#6e6e73',
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    mb: 0.75,
    display: 'block',
};

// ─── Constants ────────────────────────────────────────────────────────────────
// Far-future sentinel for "indefinite" pause — resume-cascade always handles
// the unpause, the expiry date is never reached in practice.
// ASSUMPTION: pause-cascade requires a date string; null may be rejected by BE.
const PAUSE_INDEFINITE_UNTIL = '9999-12-31T23:59:59Z';

const EXISTING_TASK_OPTIONS = [
    { value: 'just_add',    label: 'Just add',     icon: 'mdi:plus-circle-outline',    color: '#00AB55' },
    { value: 'pause_all',   label: 'Pause all',    icon: 'mdi:pause-circle-outline',   color: '#FFAB00' },
    { value: 'complete_all',label: 'Complete all', icon: 'mdi:check-circle-outline',   color: '#078DEE' },
];

// ─── Component ────────────────────────────────────────────────────────────────

const ScheduleTaskReminder = ({
    practiceId,
    leadId,
    leadDetail,
    onTaskActivity,
    handleClose,
    handleReloadLeads,
    activeTaskCount,
    activeTaskIds,
    leadTreatmentId,
}) => {
    const { currentTreatmentDataGlobal } = useSelector((state) => state.multiTreatment);

    // ── WHEN ──────────────────────────────────────────────────────────────────
    const [dateChip, setDateChip] = useState('Tomorrow');
    const [dueAt, setDueAt] = useState(() => getTomorrow());

    const handleDateChip = (chip) => {
        setDateChip(chip.label);
        if (chip.getValue) {
            setDueAt(chip.getValue());
        }
        // Custom: keep the existing value in the datetime field; user edits it.
    };

    // Respect OS-level reduced-motion preference for icon animations.
    const reducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');

    // ── HOW ───────────────────────────────────────────────────────────────────
    // Three curated V2 action types replace the raw schema/enum list.
    // Default: LOG_PHONE_CALL (first chip = "Call").
    const [selectedActionType, setSelectedActionType] = useState('LOG_PHONE_CALL');

    // ── ASSIGN TO ─────────────────────────────────────────────────────────────
    const [assignees, setAssignees] = useState([]);

    // ── NOTE ──────────────────────────────────────────────────────────────────
    const [note, setNote] = useState('');

    // ── MORE OPTIONS ──────────────────────────────────────────────────────────
    const [moreOpen, setMoreOpen] = useState(false);
    const leadFirstName = capitalizeFirstLetter(leadDetail?.first_name || '');
    const [title, setTitle] = useState('');
    const [category, setCategory] = useState(TASK_CATEGORY.FOLLOW_UP);
    const [priority, setPriority] = useState(TASK_PRIORITY.NORMAL);

    // ── EXISTING TASKS choice ─────────────────────────────────────────────────
    // 'just_add' | 'pause_all' | 'complete_all' — shown only when activeTaskCount > 0.
    const [existingTasksChoice, setExistingTasksChoice] = useState('just_add');

    // ── Saving state ──────────────────────────────────────────────────────────
    const [saving, setSaving] = useState(false);

    // ── Creation confirmation ─────────────────────────────────────────────────
    // Set after createTask success; triggers brief animated confirmation card
    // before the form closes (~1.8s).
    const [confirmSummary, setConfirmSummary] = useState(null);

    // Stack the When/How two-column row on narrow widths.
    const isNarrow = useMediaQuery('(max-width: 600px)');

    // ── Guards ────────────────────────────────────────────────────────────────
    if (!hasFeatureAccess('tasks-v2', practiceId)) return null;
    if (!leadId) return null;

    // ── Submit ────────────────────────────────────────────────────────────────
    const handleSchedule = async () => {
        if (!dueAt) {
            dispatch(updateError({ success: false, message: 'Please choose a date and time.' }));
            return;
        }

        // Convert the datetime-local string (YYYY-MM-DDTHH:mm) to a Date, then
        // produce a proper ISO 8601 string with local offset for the backend.
        const localDate = new Date(dueAt);
        if (Number.isNaN(localDate.getTime())) {
            dispatch(updateError({ success: false, message: 'Invalid date. Please pick again.' }));
            return;
        }

        const resolvedTitle = title.trim() || (leadFirstName ? `Follow-up: ${leadFirstName}` : 'Follow-up');

        // Prefer the treatment id passed by the parent (the active treatment the
        // user is working in). Only fall back to the global/first-treatment guess
        // when no explicit treatment context was provided — otherwise pause/create
        // leak to the wrong treatment (or lead-wide) on multi-treatment leads.
        const resolvedTreatmentId =
            leadTreatmentId ||
            currentTreatmentDataGlobal?.treatmentData?.id ||
            leadDetail?.lead_treatments?.[0]?.id ||
            null;

        // Map the selected V2 action type to the TaskMethod value the backend
        // understands.  The Task model has no action_type / item_type field —
        // those live on ChecklistItems.  If the backend adds a top-level
        // action_type field in future, swap METHOD_FOR_ACTION[selectedActionType]
        // for a direct `action_type: selectedActionType` field.
        const mappedMethod = METHOD_FOR_ACTION[selectedActionType] || TASK_METHOD.CALL;

        const payload = {
            title: resolvedTitle,
            ...(note.trim() && { description: note.trim() }),
            due_at: localDate.toISOString(), // eslint-disable-line camelcase
            priority,
            category,
            method: mappedMethod,
            assignees: assignees.map((a) => ({ practice_user_id: a.id })), // eslint-disable-line camelcase
            lead: leadId,
            ...(resolvedTreatmentId && { lead_treatment: resolvedTreatmentId }), // eslint-disable-line camelcase
        };

        setSaving(true);
        dispatch(
            createTask(practiceId, payload, (result) => {
                if (!result?.success) {
                    setSaving(false);
                    return;
                }

                // Capture the form state for the confirmation card NOW, before any
                // async cascade modifies it.  The selectedActionType drives the badge icon.
                const confirmedOpt = ACTION_TYPE_OPTIONS.find((o) => o.actionType === selectedActionType) || ACTION_TYPE_OPTIONS[0];
                const confirmedTitle = payload.title;
                const confirmedDueAt = new Date(parseApiDate(payload.due_at));
                const confirmedAssignees = assignees.slice();

                // Shared close sequence — called after any cascade fires (or immediately
                // for 'just_add'). Show the confirmation card for ~1.8s then close.
                // NOTE: handleReloadLeads is deferred into the timeout so it fires AFTER
                // the animation has played — calling it synchronously before setConfirmSummary
                // could trigger a parent re-render that races with the overlay mount.
                const finishUp = () => {
                    setSaving(false);
                    if (onTaskActivity) onTaskActivity();
                    // Show the animated confirmation summary FIRST — before any reload
                    // that might disrupt the component tree.
                    setConfirmSummary({
                        icon: confirmedOpt.icon,
                        color: confirmedOpt.color,
                        actionType: confirmedOpt.actionType,
                        label: confirmedOpt.label,
                        title: confirmedTitle,
                        dueAt: confirmedDueAt,
                        assignees: confirmedAssignees,
                    });
                    setTimeout(() => {
                        setConfirmSummary(null);
                        // Reload after animation so data is fresh when the next modal opens.
                        if (handleReloadLeads) handleReloadLeads();
                        if (handleClose) handleClose();
                    }, 1800);
                };

                if (existingTasksChoice === 'pause_all') {
                    // Pause all checklist items on this lead indefinitely.
                    // NOTE: standalone tasks (template_id=null) are NOT affected by
                    // pause-cascade — they remain OPEN. This is a known v1 gap.
                    // treatment-scoped to avoid cross-treatment pause leak: this component
                    // operates in a single treatment context so we pass the treatment PK.
                    dispatch(pauseLeadCascade(practiceId, leadId, { pausedUntil: PAUSE_INDEFINITE_UNTIL, leadTreatmentId: resolvedTreatmentId }, finishUp));
                } else if (existingTasksChoice === 'complete_all' && (activeTaskIds || []).length > 0) {
                    // Per-task complete loop — NOT front-mirror-blocked (unlike bulk-complete).
                    dispatch(completeTasksInParallel(practiceId, activeTaskIds, finishUp));
                } else {
                    finishUp();
                }
            })
        );
    };

    // ── Render ────────────────────────────────────────────────────────────────
    return (
        <Box
            sx={{
                // Match the UP NEXT card (UpNextCardV2): white card, 16px radius,
                // the same layered subtle shadow. Compact padding.
                p: 2,
                bgcolor: '#ffffff',
                borderRadius: '16px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.04), 0 8px 24px rgba(0,0,0,0.06)',
                // position:relative needed for the creation-confirmation absolute overlay.
                position: 'relative',
                overflow: 'hidden',
            }}
        >
            {/* Header — compact, with a yellow animated calendar icon (card-style). */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 1.75 }}>
                <Box sx={{
                    width: 38,
                    height: 38,
                    borderRadius: '10px',
                    bgcolor: 'rgba(255,171,0,0.12)',
                    border: '1px solid rgba(255,171,0,0.30)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    '@keyframes reminderPulse': {
                        '0%': { boxShadow: '0 0 0 0 rgba(255,171,0,0.45)' },
                        '70%': { boxShadow: '0 0 0 7px rgba(255,171,0,0)' },
                        '100%': { boxShadow: '0 0 0 0 rgba(255,171,0,0)' },
                    },
                    animation: 'reminderPulse 1.8s ease-out infinite',
                    '& svg': { animation: 'reminderWiggle 1.8s ease-in-out infinite', transformOrigin: 'center' },
                    '@keyframes reminderWiggle': {
                        '0%, 65%, 100%': { transform: 'rotate(0deg)' },
                        '75%': { transform: 'rotate(-10deg)' },
                        '85%': { transform: 'rotate(10deg)' },
                    },
                    '@media (prefers-reduced-motion: reduce)': { animation: 'none', '& svg': { animation: 'none' } },
                }}>
                    <Iconify icon="mdi:calendar-clock" width={18} height={18} sx={{ color: '#FFAB00' }} />
                </Box>
                <Box>
                    <Typography sx={{ fontSize: '15px', fontWeight: 700, color: '#1d1d1f', letterSpacing: '-0.015em', lineHeight: 1.2 }}>
                        Schedule a follow-up
                    </Typography>
                    <Typography sx={{ fontSize: '12px', color: '#6e6e73' }}>
                        Create a task reminder for this lead.
                    </Typography>
                </Box>
            </Box>

            {/* Top two-column row: LEFT = When (+ Note), RIGHT = How (+ Assign to).
                Stacks vertically on narrow widths. */}
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: isNarrow ? 'column' : 'row',
                    gap: isNarrow ? 1.75 : 2,
                    mb: 1.75,
                }}
            >
                {/* LEFT column — WHEN + TITLE + NOTE */}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    {/* WHEN */}
                    <Box sx={{ mb: 1.75 }}>
                        <Typography component="span" sx={LABEL_SX}>When</Typography>
                        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ rowGap: 0.5 }}>
                            {DATE_CHIPS.map((chip) => (
                                <Box
                                    key={chip.label}
                                    onClick={() => handleDateChip(chip)}
                                    sx={chipSx(dateChip === chip.label)}
                                >
                                    {chip.label}
                                </Box>
                            ))}
                        </Stack>
                        {dateChip === 'Custom' && (
                            <TextField
                                type="datetime-local"
                                value={dueAt}
                                onChange={(e) => setDueAt(e.target.value)}
                                size="small"
                                fullWidth
                                InputLabelProps={{ shrink: true }}
                                inputProps={{ min: toDatetimeLocalString(new Date()) }}
                                sx={{ mt: 1 }}
                            />
                        )}
                    </Box>

                    {/* TITLE — always visible above Note; auto-fills when blank on submit */}
                    <Box sx={{ mb: 1.75 }}>
                        <Typography component="span" sx={LABEL_SX}>Title</Typography>
                        <TextField
                            fullWidth
                            size="small"
                            placeholder={leadFirstName ? `Follow-up: ${leadFirstName}` : 'Follow-up'}
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                        />
                    </Box>

                    {/* NOTE */}
                    <Box>
                        <Typography component="span" sx={LABEL_SX}>Note (optional)</Typography>
                        <TextField
                            multiline
                            minRows={2}
                            maxRows={5}
                            fullWidth
                            size="small"
                            placeholder="Add a note…"
                            value={note}
                            onChange={(e) => setNote(e.target.value)}
                        />
                    </Box>
                </Box>

                {/* RIGHT column — HOW + ASSIGN TO */}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    {/* HOW — animated icon badge cards (same design language as UpNextCardV2) */}
                    <Box sx={{ mb: 1.75 }}>
                        <Typography component="span" sx={LABEL_SX}>How</Typography>
                        <Stack direction="row" spacing={1.5}>
                            {ACTION_TYPE_OPTIONS.map((opt) => {
                                const active = selectedActionType === opt.actionType;
                                const animClass = active ? getIconAnimationClass(opt.actionType) : null;
                                const animSx = animClass ? getIconAnimSx(animClass, reducedMotion) : {};
                                return (
                                    <Box
                                        key={opt.actionType}
                                        onClick={() => setSelectedActionType(opt.actionType)}
                                        sx={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            gap: 0.5,
                                            cursor: 'pointer',
                                            userSelect: 'none',
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                width: 44,
                                                height: 44,
                                                borderRadius: '12px',
                                                bgcolor: `${opt.color}1A`,
                                                border: active
                                                    ? `2px solid ${opt.color}`
                                                    : `1.5px solid ${opt.color}33`,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                transition: 'border-color 150ms',
                                                ...animSx,
                                            }}
                                        >
                                            <Iconify icon={opt.icon} width={22} height={22} sx={{ color: opt.color }} />
                                        </Box>
                                        <Typography sx={{
                                            fontSize: '11px',
                                            fontWeight: active ? 600 : 400,
                                            color: active ? opt.color : '#6e6e73',
                                            lineHeight: 1.2,
                                            transition: 'color 150ms',
                                        }}>
                                            {opt.label}
                                        </Typography>
                                    </Box>
                                );
                            })}
                        </Stack>
                    </Box>

                    {/* ASSIGN TO */}
                    <Box>
                        <AssignedToLead
                            label="ASSIGN TO"
                            assignToState={assignees}
                            setAssignTo={setAssignees}
                            staffRole="assignee"
                            showSelectAll
                        />
                    </Box>
                </Box>
            </Box>

            {/* EXISTING TASKS ────────────────────────────────────────────────
                Shown only when the lead already has ≥1 active (OPEN) task.
                Three choices: Just add (default) / Pause all / Complete all.
                "Pause all" fires pause-cascade (checklist items only — standalone
                tasks remain OPEN, v1 gap). "Complete all" fires bulk-complete
                (covers both). "Just add" leaves existing tasks untouched. */}
            {activeTaskCount > 0 && (
                <Box sx={{ mb: 1.75 }}>
                    <Typography component="span" sx={LABEL_SX}>
                        Existing tasks ({activeTaskCount})
                    </Typography>
                    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ rowGap: 0.5 }}>
                        {EXISTING_TASK_OPTIONS.map((opt) => {
                            const active = existingTasksChoice === opt.value;
                            return (
                                <Box
                                    key={opt.value}
                                    onClick={() => setExistingTasksChoice(opt.value)}
                                    sx={chipSx(active, opt.color)}
                                >
                                    <Iconify
                                        icon={opt.icon}
                                        width={13}
                                        height={13}
                                        sx={{ color: active ? opt.color : '#6e6e73' }}
                                    />
                                    {opt.label}
                                </Box>
                            );
                        })}
                    </Stack>
                    {existingTasksChoice === 'pause_all' && (
                        <Typography sx={{ fontSize: '11px', color: '#FFAB00', mt: 0.5, lineHeight: 1.4 }}>
                            Checklist tasks will be paused until manually unpaused.
                        </Typography>
                    )}
                    {existingTasksChoice === 'complete_all' && (
                        <Typography sx={{ fontSize: '11px', color: '#078DEE', mt: 0.5, lineHeight: 1.4 }}>
                            All active tasks will be marked as complete.
                        </Typography>
                    )}
                </Box>
            )}

            {/* MORE OPTIONS ─────────────────────────────────────────────────── */}
            <Box sx={{ mb: 1.75 }}>
                <Button
                    size="small"
                    onClick={() => setMoreOpen(!moreOpen)}
                    endIcon={<Iconify icon={moreOpen ? 'eva:chevron-up-fill' : 'eva:chevron-down-fill'} width={14} height={14} />}
                    sx={{
                        color: '#0071e3',
                        fontSize: '12px',
                        fontWeight: 500,
                        textTransform: 'none',
                        letterSpacing: '-0.005em',
                        px: 0,
                        '&:hover': { bgcolor: 'transparent', textDecoration: 'underline' },
                    }}
                >
                    More options
                </Button>
                <Collapse in={moreOpen} timeout={250}>
                    <Stack spacing={1.25} sx={{ mt: 1.25 }}>
                        {/* Category */}
                        <Box>
                            <Typography component="span" sx={LABEL_SX}>Category</Typography>
                            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ rowGap: 0.5 }}>
                                {[
                                    { label: 'Follow Up', value: TASK_CATEGORY.FOLLOW_UP },
                                    { label: 'Patient Care', value: TASK_CATEGORY.PATIENT_CARE },
                                    { label: 'Lead & Marketing', value: TASK_CATEGORY.LEAD_MARKETING },
                                    { label: 'Operational', value: TASK_CATEGORY.OPERATIONAL },
                                ].map((opt) => (
                                    <Box key={opt.value} onClick={() => setCategory(opt.value)} sx={chipSx(category === opt.value)}>
                                        {opt.label}
                                    </Box>
                                ))}
                            </Stack>
                        </Box>
                        {/* Priority */}
                        <Box>
                            <Typography component="span" sx={LABEL_SX}>Priority</Typography>
                            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ rowGap: 0.5 }}>
                                {[
                                    { label: 'Urgent', value: TASK_PRIORITY.URGENT },
                                    { label: 'High', value: TASK_PRIORITY.HIGH },
                                    { label: 'Normal', value: TASK_PRIORITY.NORMAL },
                                    { label: 'Low', value: TASK_PRIORITY.LOW },
                                ].map((opt) => (
                                    <Box key={opt.value} onClick={() => setPriority(opt.value)} sx={chipSx(priority === opt.value)}>
                                        {opt.label}
                                    </Box>
                                ))}
                            </Stack>
                        </Box>
                    </Stack>
                </Collapse>
            </Box>

            {/* Submit ───────────────────────────────────────────────────────── */}
            <LoadingButton
                loading={saving}
                loadingPosition="start"
                startIcon={<Iconify icon="solar:bell-bold" width={16} height={16} />}
                onClick={handleSchedule}
                disabled={!dueAt || saving}
                fullWidth
                sx={{
                    bgcolor: '#00AB55',
                    color: '#ffffff',
                    fontSize: '13px',
                    fontWeight: 600,
                    letterSpacing: '-0.01em',
                    textTransform: 'none',
                    borderRadius: '8px',
                    boxShadow: 'none',
                    py: 1,
                    '&:hover': { bgcolor: '#02934A', boxShadow: 'none' },
                    '&:active': { bgcolor: '#017A3D' },
                    '&.Mui-disabled': { bgcolor: 'rgba(0, 171, 85, 0.35)', color: '#ffffff' },
                }}
            >
                Schedule reminder
            </LoadingButton>

            {/* ── Creation confirmation overlay ───────────────────────────────
                Shown for ~1.8s after createTask succeeds, then form closes.
                Overlays the entire card with the new task's icon/title/when/assignees. */}
            {confirmSummary && (
                <Box
                    sx={{
                        position: 'absolute',
                        inset: 0,
                        borderRadius: '16px',
                        bgcolor: '#ffffff',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 1.5,
                        p: 2.5,
                        // zIndex ensures the overlay sits above all card content.
                        zIndex: 10,
                        // Single lifecycle animation: fade-in (0-14%) → hold (14-80%) → fade-out (80-100%).
                        // A single keyframe avoids the CSS animation priority conflict that occurs
                        // when two animations target the same property simultaneously (the last
                        // listed always wins, which was silently cancelling the fade-in).
                        '@keyframes confirm-lifecycle': {
                            '0%':   { opacity: 0, transform: 'scale(0.94)' },
                            '14%':  { opacity: 1, transform: 'scale(1)' },
                            '80%':  { opacity: 1, transform: 'scale(1)' },
                            '100%': { opacity: 0, transform: 'scale(0.96)' },
                        },
                        animation: 'confirm-lifecycle 1.8s cubic-bezier(0.32,0.72,0,1) forwards',
                    }}
                >
                    {/* Checkmark badge */}
                    <Box sx={{
                        width: 44, height: 44, borderRadius: '50%',
                        bgcolor: 'rgba(52,199,89,0.12)',
                        border: '2px solid rgba(52,199,89,0.6)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        ...getIconAnimSx('pulse-scale', reducedMotion),
                    }}>
                        <Iconify icon="mdi:check-circle" width={24} height={24} sx={{ color: '#34c759' }} />
                    </Box>
                    {/* "Scheduled!" label */}
                    <Typography sx={{ fontSize: '15px', fontWeight: 700, color: '#1d1d1f', letterSpacing: '-0.015em', textAlign: 'center' }}>
                        Reminder scheduled
                    </Typography>
                    {/* Task summary card */}
                    <Box sx={{
                        display: 'flex', alignItems: 'center', gap: 1.5,
                        bgcolor: `${confirmSummary.color}0D`,
                        border: `1.5px solid ${confirmSummary.color}33`,
                        borderRadius: '12px', px: 2, py: 1.25, width: '100%', maxWidth: 320,
                    }}>
                        {/* Method icon badge */}
                        <Box sx={{
                            width: 36, height: 36, borderRadius: '9px',
                            bgcolor: `${confirmSummary.color}1A`,
                            border: `1.5px solid ${confirmSummary.color}33`,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            flexShrink: 0,
                            ...getIconAnimSx(getIconAnimationClass(confirmSummary.actionType), reducedMotion),
                        }}>
                            <Iconify icon={confirmSummary.icon} width={18} height={18} sx={{ color: confirmSummary.color }} />
                        </Box>
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                            <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#1d1d1f', letterSpacing: '-0.01em', lineHeight: 1.3,
                                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {confirmSummary.title}
                            </Typography>
                            <Typography sx={{ fontSize: '11px', color: '#6e6e73', lineHeight: 1.4, mt: 0.25 }}>
                                {confirmSummary.dueAt.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })}
                                {' · '}
                                {confirmSummary.dueAt.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}
                            </Typography>
                        </Box>
                    </Box>
                    {/* Assignees (if set) */}
                    {confirmSummary.assignees.length > 0 && (
                        <Typography sx={{ fontSize: '11px', color: '#6e6e73' }}>
                            Assigned to {confirmSummary.assignees.map((a) => a.name || a.user?.first_name || 'team').join(', ')}
                        </Typography>
                    )}
                </Box>
            )}
        </Box>
    );
};

ScheduleTaskReminder.propTypes = {
    /** The practice ID — used for the createTask endpoint and feature-flag check. */
    practiceId: PropTypes.number,
    /** Lead ID to attach the task to. */
    leadId: PropTypes.number,
    /** Full lead object from Redux (followLead). Used for auto-title and treatment resolution. */
    leadDetail: PropTypes.object,
    /**
     * Called (no args) after a task is created successfully. Used by the
     * attended popups to auto-select the "Reminder & Follow-up" outcome chip,
     * which unlocks the Finish button.
     */
    onTaskActivity: PropTypes.func,
    /** Called to close the parent popup after scheduling. */
    handleClose: PropTypes.func,
    /** Called to trigger a table/data reload after scheduling. */
    handleReloadLeads: PropTypes.func,
    /**
     * Count of currently active (OPEN) tasks on this lead — drives the
     * "Existing tasks (N)" section visibility. 0 or absent = section hidden.
     */
    activeTaskCount: PropTypes.number,
    /**
     * Deduplicated task IDs of active tasks — used by bulk-complete when
     * the user picks "Complete all". Order is not significant.
     */
    activeTaskIds: PropTypes.arrayOf(PropTypes.number),
    /** Active LeadTreatment PK — scopes the created task + pause-cascade to this
     *  treatment. Falls back to a global/first-treatment guess when omitted. */
    leadTreatmentId: PropTypes.number,
};

ScheduleTaskReminder.defaultProps = {
    practiceId: null,
    leadId: null,
    leadDetail: null,
    onTaskActivity: null,
    handleClose: null,
    handleReloadLeads: null,
    activeTaskCount: 0,
    activeTaskIds: [],
    leadTreatmentId: null,
};

export default ScheduleTaskReminder;
